/**
 * The shell's catalog of every declared setting (I-2510): Connect's own
 * sections (`connectSettingsSections.ts`) at once, the apps' once the list
 * asks for them — every app whose entry sets `settings: true` and the
 * account may use hands its `settingsSections` (and the pages a design can
 * arrange, `settingsPages`) over in its `@<app>/connect` module, which the
 * shell loads like the hub does. Each app's texts are translated with the
 * app's own table (I-2284). `main.ts` provides the one
 * catalog app-wide (`connectSettingsCatalogKey`): the settings page, the look
 * designer and the admin app read it.
 *
 * Connect's own sections also hold the rows only master knows (I-2915): what
 * the notifications of each module and type do per device type, declared
 * from master's type register when the catalog loads
 * (`notificationSettings.ts`). A register that cannot be read leaves the
 * declared rows as they are. The same holds for the accounts of the section
 * „Startseite" (`homeUnreadSettings.ts`), from master's available accounts.
 */
import type { Component } from 'vue';
import type { Router } from 'vue-router';
import {
  createConnectSettingsCatalog,
  type ConnectAppModule,
  type ConnectSettingsCatalog,
  type ConnectSettingsDeclarationSource,
} from '@blpde/connect';
import i18n from '@/i18n';
import { appMessages, appTranslator, withAppI18n, type Translator } from '@/i18n/appI18n';
import { apps, type AppEntry, type AppId } from '@/integration';
import { fetchNotificationTypes, type NotificationType } from '@/notifications';
import type { SharedAccount } from '@blpde/connect/auth';
import { connectSettingsSections } from './connectSettingsSections';
import { fetchHomeUnreadAccounts, homeUnreadAccountTranslator, withHomeUnreadAccounts } from './homeUnreadSettings';
import { notificationSettingsTranslator, withNotificationSettings } from './notificationSettings';

type Install = (id: AppId) => Promise<ConnectAppModule>;

/** Connect's own table. */
function shellMessages(): Translator {
  const { t, te } = i18n.global;
  return {
    t: (key, named, plural) => {
      if (plural !== undefined) return t(key, named ?? {}, plural);
      return named ? t(key, named) : t(key);
    },
    te: (key) => te(key),
  };
}

/**
 * Connect's own sections, captioned from the shell's table. With master's
 * notification types and the apps the account may use, the section „Benachrichtigungen"
 * also holds their settings (`notificationSettings.ts`), each type captioned
 * from the table of the app that declares it.
 */
export function shellDeclarationSource(
  types: readonly NotificationType[] = [],
  entries: readonly AppEntry[] = apps,
  mailAccounts: readonly SharedAccount[] = []
): ConnectSettingsDeclarationSource {
  const shell = shellMessages();
  const own = withHomeUnreadAccounts(connectSettingsSections, mailAccounts);
  if (!types.length) {
    return { sections: own, t: homeUnreadAccountTranslator(mailAccounts, shell.t) };
  }
  const appOf = (moduleKey: string) => apps.find((entry) => entry.id === moduleKey);
  const allowed = new Set<string>(entries.map((entry) => entry.id));
  return {
    sections: withNotificationSettings(own, types, {
      find: appOf,
      entitled: (id) => allowed.has(id),
      order: apps.map((entry) => entry.id),
    }),
    t: homeUnreadAccountTranslator(
      mailAccounts,
      notificationSettingsTranslator(types, {
        shell,
        app: (moduleKey) => {
          const app = appOf(moduleKey);
          return app ? appMessages(app.id) : undefined;
        },
        appLabelKey: (moduleKey) => appOf(moduleKey)?.labelKey,
      })
    ),
  };
}

/** master's notification types; none when the register cannot be read. */
async function loadNotificationTypes(load: () => Promise<NotificationType[]>): Promise<NotificationType[]> {
  try {
    return await load();
  } catch (err) {
    console.warn('[settings] could not load the notification types', err);
    return [];
  }
}

/** The accounts for the mail count's rows; none when master cannot tell. */
async function loadHomeUnreadAccounts(load: () => Promise<SharedAccount[]>): Promise<SharedAccount[]> {
  try {
    return await load();
  } catch (err) {
    console.warn('[settings] could not load the accounts of the mail count', err);
    return [];
  }
}

/**
 * Connect's own sections, then every contributing app's in the shell's app
 * order. An app whose module fails to load is left out; the rest still show.
 */
export async function loadSettingsDeclarations(
  entries: readonly AppEntry[],
  install: Install,
  notificationTypes: () => Promise<NotificationType[]> = fetchNotificationTypes,
  mailAccounts: () => Promise<SharedAccount[]> = fetchHomeUnreadAccounts
): Promise<ConnectSettingsDeclarationSource[]> {
  const types = loadNotificationTypes(notificationTypes);
  const accounts = loadHomeUnreadAccounts(mailAccounts);
  const loaded = await Promise.all(
    entries
      .filter((entry) => entry.settings)
      .map(async (entry): Promise<ConnectSettingsDeclarationSource | null> => {
        try {
          const module = await install(entry.id);
          const sections = module.settingsSections ?? [];
          const pages = module.settingsPages ?? [];
          const describe = module.describeSettingsDocument;
          const editor = module.settingsPageEditor;
          // The app's editor renders with the app's own table (I-2284).
          const pageEditor = editor
            ? () =>
                editor().then((loaded) =>
                  withAppI18n(entry.id, 'default' in loaded ? (loaded.default as Component) : loaded)
                )
            : undefined;
          return sections.length || pages.length || describe
            ? {
                app: entry.id,
                sections,
                pages,
                ...(describe ? { describe } : {}),
                ...(pageEditor ? { pageEditor } : {}),
                t: appTranslator(entry.id),
              }
            : null;
        } catch (err) {
          console.warn('[settings] could not load the declared settings of', entry.id, err);
          return null;
        }
      })
  );
  return [
    shellDeclarationSource(await types, entries, await accounts),
    ...loaded.filter((source): source is ConnectSettingsDeclarationSource => source !== null),
  ];
}

/** The catalog `main.ts` provides: the apps the account may use, loaded through the shell's loader. */
export function createShellSettingsCatalog(
  router: Router,
  install: (router: Router, id: AppId) => Promise<ConnectAppModule>,
  entitled: (id: AppId) => boolean
): ConnectSettingsCatalog {
  return createConnectSettingsCatalog(
    () =>
      loadSettingsDeclarations(
        apps.filter((entry) => entitled(entry.id)),
        (id) => install(router, id)
      ),
    [shellDeclarationSource()]
  );
}
